import { clsx } from '@alexis/helpers/clsx';
import { translateNumber, translateText } from '@alexis/helpers/translation';
import { datadogRum } from '@datadog/browser-rum';
import { useFilterToggle } from '@pages/HotelSearchResultV2/components/QuickFilters/hooks/useFilterToggle';
import React, { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Price from '@components/Price';

import { NEW_OTA_DESIGN } from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import {
  getReviewerGroupFilterTranslation,
  getReviewRatingTypeDisplay,
  getReviewScoreDisplay,
} from '@helpers/review';

import InvaliDateIcon from '@icons/invalid_date.svg';
import MaxSearchLimitIcon from '@icons/max_search_limit.svg';
import NoInternetIcon from '@icons/no_internet_connection.svg';
import NoResultIcon from '@icons/no_result.svg';
import SomethingWrongIcon from '@icons/something_wrong.svg';
import CloseIcon from '@icons/x-close-small.svg';

import {
  getTranslationsState,
  getLocaleState,
  getCurrencyState,
  getExchangeRateState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import { Translations } from '@wegoTypes/translations';

import styles from './AlertMessage.module.scss';

export enum MessageTypes {
  NO_RESULTS = 'NO_RESULTS',
  INVALID_DATE = 'INVALID_DATE',
  NO_INTERNET = 'NO_INTERNET',
  SOMETHING_WRONG = 'SOMETHING_WRONG',
  MAX_SEARCH_LIMIT = 'MAX_SEARCH_LIMIT',
}

interface AlertMessageProps {
  buttonLabel?: string;
  className?: string;
  error?: Error;
  message: string;
  title?: string;
  type: MessageTypes;
  onClick?: () => void;
  showFilters?: boolean;
}

const AlertMessage: React.FC<AlertMessageProps> = ({
  buttonLabel,
  className,
  error,
  message,
  title,
  type,
  onClick,
  showFilters,
}) => {
  const { userCentricCoreConfigs } = useContext(GlobalContext);
  const locale: string = useSelector(getLocaleState);
  const translations: Translations = useSelector(getTranslationsState);
  const currency: Currency = useSelector(getCurrencyState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);

  const { selectedFilters, selectedUsdPrices, onUsdPricesChanged, minUsdPrice, maxUsdPrice } =
    useFilterToggle();

  const newOtaDesignConfig = userCentricCoreConfigs.find(
    (coreConfig) => coreConfig.key === NEW_OTA_DESIGN,
  )?.value;

  const newOtaDesign = newOtaDesignConfig === '1' || newOtaDesignConfig === '2';

  const isPriceRangeActive =
    selectedUsdPrices[0] !== minUsdPrice || selectedUsdPrices[1] !== maxUsdPrice;
  const isPriceValid =
    !isNaN(minUsdPrice) && !isNaN(maxUsdPrice) && isFinite(minUsdPrice) && isFinite(maxUsdPrice);

  const getIconByType = (type: MessageTypes) => {
    switch (type) {
      case MessageTypes.NO_RESULTS:
        return NoResultIcon;
      case MessageTypes.INVALID_DATE:
        return InvaliDateIcon;
      case MessageTypes.NO_INTERNET:
        return NoInternetIcon;
      case MessageTypes.SOMETHING_WRONG:
        return SomethingWrongIcon;
      case MessageTypes.MAX_SEARCH_LIMIT:
        return MaxSearchLimitIcon;
      default:
        return NoResultIcon;
    }
  };

  const errorId = useMemo(() => {
    return crypto.randomUUID();
  }, []);

  // When passed with the error prop, send it to datadog for easier finding of error logs
  useEffect(() => {
    if (error) {
      const errorContext = {
        errorType: type,
        errorMessage: error.message,
        errorTitle: title,
        errorId,
      };
      datadogRum.addError(error, errorContext);
    }
  }, []);

  const Icon = getIconByType(type);

  return (
    <div className={clsx(styles.container, className, newOtaDesign && styles.newOtaDesign)}>
      <Icon />

      {!!title ? <div className={styles.title}>{title}</div> : null}

      {/* used for debugging with datadog. Dont need translation */}
      {!newOtaDesign && <div className={styles.message}>Error ID: {errorId}</div>}

      <div className={styles.message}>{message}</div>

      {showFilters && (
        <div className={styles.filters}>
          {isPriceRangeActive && isPriceValid ? (
            <div className={styles.chip}>
              <Price
                isInline
                className={styles.price}
                price={{
                  amount: selectedUsdPrices[0],
                  amountUsd: selectedUsdPrices[0],
                  currencyCode: 'USD',
                }}
                locale={locale}
                currency={currency}
                exchangeRate={exchangeRate}
              />
              <span className={styles.dash}> &#45; </span>
              <Price
                isInline
                className={styles.price}
                price={{
                  amount: selectedUsdPrices[1],
                  amountUsd: selectedUsdPrices[1],
                  currencyCode: 'USD',
                }}
                locale={locale}
                currency={currency}
                exchangeRate={exchangeRate}
              />

              <CloseIcon onClick={() => onUsdPricesChanged([minUsdPrice, maxUsdPrice])} />
            </div>
          ) : null}

          {selectedFilters.map((filter) => {
            const identifier = filter.key === 'guest_rating' ? filter.name : filter.id;
            const reviewScoreItem = filter;
            const isLastScore = reviewScoreItem.minScore === -1;

            return (
              <div key={`${identifier}-${filter.key}`} className={styles.chip}>
                {filter.key === 'guest_rating'
                  ? getReviewerGroupFilterTranslation(translations, filter?.name as string)
                  : filter.name}
                {filter.key === 'stars' ? '-Star' : null}
                {filter.key === 'guest_score' ? (
                  <span>
                    {getReviewRatingTypeDisplay(reviewScoreItem.minScore, translations)} •{' '}
                    <strong>
                      {isLastScore
                        ? translateText(
                            translations.lbl_score_below,
                            locale,
                            translateNumber(
                              getReviewScoreDisplay(reviewScoreItem.maxScore),
                              locale === 'fa',
                            ),
                          )
                        : `${reviewScoreItem.minScore}+`}
                    </strong>
                  </span>
                ) : null}

                <CloseIcon onClick={() => filter.callback(identifier as never, false)} />
              </div>
            );
          })}
        </div>
      )}

      {buttonLabel && <button onClick={onClick}>{buttonLabel}</button>}
    </div>
  );
};

export default AlertMessage;
